import { SecretClient } from "@azure/keyvault-secrets";
import type { TokenCredential } from "@azure/core-auth";

export interface AppSecrets {
  appId: string;       // numeric string
  slug: string;        // App URL slug (e.g. "m8t-brain")
  privateKeyPem: string;
}

const SECRET_NAMES = {
  appId: "github-app-id",
  slug: "github-app-slug",
  privateKeyPem: "github-app-private-key",
} as const;

// Per-process cache, keyed by kvUri. App secrets rotate rarely (manual
// operator action); a process restart picks up new values. Acceptable
// for both gateway (Container App replica) and plugin (Claude Code session).
const cache = new Map<string, Promise<AppSecrets>>();

/** TEST-ONLY: clear the cache between tests. */
export function _resetAppSecretsCache(): void {
  cache.clear();
}

async function fetchAllSecrets(
  credential: TokenCredential,
  kvUri: string,
): Promise<AppSecrets> {
  const client = new SecretClient(kvUri, credential);
  const [appId, slug, pem] = await Promise.all([
    client.getSecret(SECRET_NAMES.appId),
    client.getSecret(SECRET_NAMES.slug),
    client.getSecret(SECRET_NAMES.privateKeyPem),
  ]);
  const v = (sec: { value?: string }, name: string): string => {
    if (typeof sec.value !== "string" || sec.value.length === 0) {
      throw new Error(`readAppSecrets: secret '${name}' is missing or empty in ${kvUri}`);
    }
    return sec.value;
  };
  return {
    appId: v(appId, SECRET_NAMES.appId),
    slug: v(slug, SECRET_NAMES.slug),
    privateKeyPem: v(pem, SECRET_NAMES.privateKeyPem),
  };
}

/**
 * Read the three GitHub App secrets from Key Vault, with per-process caching
 * keyed by kvUri. Idempotent — concurrent calls share one in-flight promise.
 *
 * Throws on missing/empty secret values; callers (m8t brain check-app +
 * inline link-cascade precondition) surface a fix-up-your-KV message.
 */
export function readAppSecrets(args: {
  credential: TokenCredential;
  kvUri: string;
}): Promise<AppSecrets> {
  const existing = cache.get(args.kvUri);
  if (existing) return existing;
  const promise = fetchAllSecrets(args.credential, args.kvUri).catch((e: unknown) => {
    // On failure, evict so the next call retries (avoid permanent breakage
    // from a transient KV glitch).
    cache.delete(args.kvUri);
    throw e;
  });
  cache.set(args.kvUri, promise);
  return promise;
}
